// Layer 3 (Transformation): the project home (polish.P23 #710, locked comp
// `.impeccable/mocks/decision/project-home.html` A, with B's plant-model header
// fact and C's cursor-anchored chat times).
//
// The page is the project's front door, not an eighth landing: a header with
// the plant's identity and design basis, one module index in the persona's
// rail order where every row carries its owning route's lead figure as a
// reference (number, basis, period), the project's chats and one Files panel
// built from the ingestion seed's artefacts.
//
// Every index figure is read from the owning route's own transform — the same
// function and inputs the route passes — so a row can never disagree with the
// route it opens (comp adjudication 3). Nothing here computes a figure a route
// does not already print; this file only picks each route's lead cell and
// lays its comparison out as one line.

import type { Persona } from '@/shared/types';
import type { SessionState, ScenarioPhase } from '@/versions/v1/schema/scenario';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import type { ClaimsSessionState } from '@/versions/v1/shared/ClaimsEngine';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import type { EscalationDecisionState } from '@/versions/v1/shared/EscalationDecisionEngine';
import type { FmeaLearningSessionState } from '@/versions/v1/shared/FmeaLearningEngine';
import type { FlowLandingRead } from '@/versions/v1/modules/advisory/FlowLanding.transform';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type { ProjectTile } from '@/versions/v1/shared/project-seed';
import type { WorkflowCatalogueRow } from '@/versions/v1/modules/projects/WorkflowCatalogue.transform';
import {
  visibleProjectNav,
  type ProjectSidebarNavItem,
} from '@/versions/v1/shared/project-sidebar-nav';
import { SEED_THREADS } from '@/versions/v1/shared/thread-seed';
import { WORKFLOW_DEFINITIONS } from '@/versions/v1/shared/workflow-catalogue-seed';
import { projectModulesSeed } from '@/versions/v1/shared/project-modules-seed';
import { foundationWorkflowsLead } from '@/versions/v1/modules/projects/ProjectWorkflowsScene.transform';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { recordedEscalationIds } from '@/versions/v1/shared/EscalationDecisionEngine';
import { DESIGN_THROUGHPUT_STPD } from '@/versions/v1/shared/bunker-hill-planning-seed';
import { RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { RECIRCULATING_LOOPS, REPORT_INSTANCES, SAMPLE_POINTS, STREAMS } from '@/versions/v1/shared/bunker-hill-seed';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { evaluateCapacity } from '@/versions/v1/shared/CapacityEngine';
import { CAPACITY_SEED } from '@/versions/v1/shared/capacity-seed';
import { defaultSmelterPriceState } from '@/versions/v1/shared/bunker-hill-smelter-terms';
// Owning-route transforms, one per index row.
import { capacityPlantState } from '@/versions/v1/modules/capacity/Scene.transform';
import { throughputProjection } from '@/versions/v1/modules/capacity/ThroughputTab.transform';
import { gmStripCells } from '@/versions/v1/modules/overview/GmLeadBand.transform';
import { suptStripCells } from '@/versions/v1/modules/overview/SuptLanding.transform';
import { cmetStripCells } from '@/versions/v1/modules/overview/ControlMetLanding.transform';
import { smetStripCells } from '@/versions/v1/modules/overview/SeniorMetLanding.transform';
import { operatorFlaggedReading } from '@/versions/v1/modules/overview/OperatorLanding.transform';
import { maintenanceEquipKpis } from '@/versions/v1/modules/overview/RoleLandingTabKpis.transform';
import {
  bunkerHillLoops,
  dashboardForRole,
  defaultPlantRole,
  plantRoleIdFromPersona,
} from '@/versions/v1/modules/overview/Scene.transform';
import { performanceForRole } from '@/versions/v1/modules/overview/Performance.transform';
import { kpiCardPropsFromTile } from '@/versions/v1/modules/_viz/KpiCard.transform';
import { casesWithSession } from '@/versions/v1/modules/advisory/Scene.transform';
import { reportTemplate, visibleReportsTabs } from '@/versions/v1/modules/reports/Scene.transform';
import { periodView } from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import {
  applySamplePhase,
  applyUnits,
  applyUtilization,
  bunkerHillSamplePoints,
  bunkerHillSubprocesses,
  bunkerHillUnits,
  bunkerHillUtilization,
  plantStrip,
  sampleChips,
} from '@/versions/v1/modules/ingestion/Scene.transform';
import { circuitsView } from '@/versions/v1/modules/ingestion/Circuits.transform';
import { plantFlowCells } from '@/versions/v1/modules/ingestion/PlantFlowStrip.transform';
import {
  bindLiveView,
  bunkerHillSources,
  contributionCards,
  reconcileView,
  setupBand,
} from '@/versions/v1/modules/ingestion/SetupScene.transform';
import { balanceStripCells, windowText } from '@/versions/v1/modules/metal-accounting/BalanceStrip.transform';
import { meetingHeaderFigures } from '@/versions/v1/modules/meeting/MeetingBoard.transform';
import { riskBand } from '@/versions/v1/modules/risk/RiskBand.transform';
import { planningBandCells } from '@/versions/v1/modules/planning/PlanningBand.transform';
import {
  annualPlanView,
  monthlyPlanView,
  rollingForecastView,
} from '@/versions/v1/modules/planning/Scene.transform';
import { costStrip } from '@/versions/v1/modules/cost/CostBand.transform';
import { costPeriod } from '@/versions/v1/modules/cost/Scene.transform';
import { hseBand, hseHeaderFact } from '@/versions/v1/modules/compliance/Scene.transform';
import {
  controlLoops,
  loopEvidenceItems,
  loopTags,
  operatorReadingsTable,
  processBand,
  readingRows,
} from '@/versions/v1/modules/process/Scene.transform';
import {
  projectRegisterTable,
  samplePipelineRead,
  samplesDueLists,
  tablesForQualityTab,
} from '@/versions/v1/modules/quality/Scene.transform';
import { maintenanceBand } from '@/versions/v1/modules/maintenance/MaintenanceBand.transform';
import { conditionMonitoringPoints } from '@/versions/v1/modules/maintenance/Scene.transform';
import { pmTasks } from '@/versions/v1/modules/maintenance/PmSchedule.transform';
import { formatHours } from '@/versions/v1/modules/maintenance/Downtime.transform';
import { downtimeImpactRead, downtimeWindow } from '@/versions/v1/modules/downtime/DowntimeImpact.transform';
import { downtimeEvents } from '@/versions/v1/modules/maintenance/DowntimeEvents.transform';
import { trainingComplianceBand } from '@/versions/v1/modules/documents/TrainingCompliance.transform';

// ---- Shared shapes -------------------------------------------------------------

/** One run of a comparison line. `strong` is the lead term (600 ink-secondary);
 *  `tone` colours only the abnormal (or closing) reading, never the figure. */
export type IndexRun = { text: string; strong?: boolean; tone?: 'ok' | 'warn' | 'bad' };

type ProductSegment = Exclude<ProjectSidebarNavItem['segment'], '' | 'chat' | 'settings'>;

export type ModuleIndexRow = {
  segment: ProductSegment;
  label: string;
  /** The rail's group caption; `groupContinues` hides it on the group's later rows. */
  group: string | null;
  groupContinues: boolean;
  /** The owning route's lead figure; empty while the route's own read is not in hand. */
  figure: string;
  /** Unit or qualifier set small on the figure's baseline. */
  unit: string | null;
  comparison: readonly IndexRun[];
};

export type ProjectHomeInput = {
  persona: Persona;
  projectSlug: string;
  state: SessionState;
  phase: ScenarioPhase;
  diagnosis: Diagnosis;
  advisory: AdvisorySessionState;
  fmeaLearning?: FmeaLearningSessionState;
  escalations: EscalationDecisionState;
  /** `money:view` — the Reports route hides its money templates without it. */
  showMoney: boolean;
  /** The Advisory route's own flow read (`useAdvisoryFlowRead().read`); null until its replay frames load. */
  advisoryFlow: FlowLandingRead | null;
  /** The Workflows route's own catalogue (`useWorkflowCatalogueRows`), rows still loading carry `loading`; empty for a project without the OptiFlow catalogue. */
  workflowCatalogue: readonly WorkflowCatalogueRow[];
};

type RowRead = Pick<ModuleIndexRow, 'figure' | 'unit' | 'comparison'>;

const CHROME = new Set<ProjectSidebarNavItem['segment']>(['', 'chat', 'settings']);

/** The agent's workflow (the definition whose runs are the K6 investigations), the one the Workflows index row names. */
const INVESTIGATION_DEFINITION_ID = WORKFLOW_DEFINITIONS.find((row) => row.runs === 'investigation')?.id;

function toneOf(tone: VizTone | 'warn' | 'bad' | 'ok' | null | undefined): IndexRun['tone'] {
  return tone === 'ok' || tone === 'warn' || tone === 'bad' ? tone : undefined;
}

/** `lead` then ` · rest`, the two-part comparison most bands carry. */
function leadRest(lead: string, rest: string): IndexRun[] {
  const runs: IndexRun[] = [];
  if (lead) runs.push({ text: lead, strong: true });
  if (rest) runs.push({ text: rest });
  return runs;
}

/** The comparison's first clause: every run up to the first ` · ` break, so a
 *  reference keeps the lead read and leaves the tail on its owning route. */
function firstClause<T extends { text: string }>(runs: readonly T[]): T[] {
  const out: T[] = [];
  for (const run of runs) {
    const cut = run.text.indexOf(' · ');
    if (cut === 0) break;
    if (cut > 0) {
      out.push({ ...run, text: run.text.slice(0, cut) });
      break;
    }
    out.push(run);
  }
  return out;
}

/** A figure string with a trailing unit (`+7.5 stph`, `163.5 h`) split so the
 *  unit sits small on the baseline, as every band does. */
function splitUnit(value: string): { figure: string; unit: string | null } {
  const match = /^(.*\d[%]?)\s+([^\d].*)$/.exec(value.trim());
  return match ? { figure: match[1]!, unit: match[2]! } : { figure: value, unit: null };
}

// ---- Header ----------------------------------------------------------------------

export type ProjectHomeHeader = {
  title: string;
  /** The plant kind: the description's first clause (`Zn-Pb-Ag concentrator`). */
  kind: string;
  facts: readonly { id: 'design' | 'model'; value: string; rest: string }[];
};

export function projectHomeHeader(project: ProjectTile, claims: ClaimsSessionState): ProjectHomeHeader {
  const version = reconcileView(claims, false).version;
  return {
    title: project.name,
    kind: project.description.split(' · ')[0]!.trim(),
    facts: [
      { id: 'design', value: `${DESIGN_THROUGHPUT_STPD.toLocaleString('en-US')} stpd`, rest: 'design' },
      {
        id: 'model',
        value: `Plant model v${version.currentN}`,
        rest: `accepted ${formatDayMonth(version.acceptedAt as Parameters<typeof formatDayMonth>[0])}`,
      },
    ],
  };
}

// ---- Module index: one reader per owning route -------------------------------------

function capacityRow(input: ProjectHomeInput): RowRead {
  const plantState = capacityPlantState(input.state);
  const read = evaluateCapacity({ grade: plantState.grade, seed: CAPACITY_SEED, prices: defaultSmelterPriceState(), plantState });
  const view = throughputProjection(read);
  const cell = (id: string) => view.ladderStrip.find((row) => row.id === id);
  const headroom = cell('headroom');
  const current = cell('current');
  const maximum = cell('maximum');
  const { figure, unit } = splitUnit(headroom?.value ?? '');
  return {
    figure,
    unit,
    comparison: view.feedLimited
      ? [{ text: 'headroom · ' }, ...leadRest(current?.value ?? '', headroom?.sub ? ` · ${headroom.sub}` : '')]
      : [
          { text: 'headroom · ' },
          { text: `${(current?.value ?? '').replace(/\s*stph$/, '')} now`, strong: true },
          // The unit already sits on the figure: `Grinding ceiling 79.2`.
          { text: ` → ${(maximum?.sub ?? '').replace(/\s*stph$/, '')}` },
        ],
  };
}

function advisoryRow(input: ProjectHomeInput): RowRead {
  const read = input.advisoryFlow;
  // The Advisory strip itself paints no figure until every case fixture's
  // frames are in hand; the index row waits the same way.
  if (!read) return { figure: '', unit: 'awaiting approval', comparison: [] };
  const rows = read.actionCaseRows;
  const bucket = (status: (typeof rows)[number]['actionStatus']) => rows.filter((row) => row.actionStatus === status).length;
  const awaiting = bucket('awaiting approval');
  const inField = bucket('in the field');
  const stops = bucket('stop planned');
  const proposals = bucket('FMEA proposal · Senior met');
  const rest = [
    ` at Action · ${inField} in the field`,
    stops ? ` · ${stops} stop planned` : '',
    proposals ? ` · ${proposals} FMEA proposal · Senior met` : '',
  ].join('');
  return {
    figure: String(awaiting),
    unit: 'awaiting approval',
    comparison: [{ text: 'of ' }, { text: String(read.counts.action), strong: true }, { text: rest }],
  };
}

/** The Overview row carries the persona landing's action count, not a
 *  performance figure: the cell each landing uses for "what needs doing". */
function overviewRow(input: ProjectHomeInput): RowRead {
  const roleId = plantRoleIdFromPersona(input.persona) ?? defaultPlantRole();
  const { state } = input;
  switch (roleId) {
    case 'gm': {
      const cell = gmStripCells(state, recordedEscalationIds(input.escalations), input.fmeaLearning)
        .find((row) => row.id === 'esc')!;
      return { figure: cell.figure, unit: cell.figureContext ?? null, comparison: leadRest(cell.comparisonLead, cell.comparisonRest) };
    }
    case 'superintendent': {
      const cell = suptStripCells(state, input.escalations).find((row) => row.id === 'issues')!;
      return {
        figure: cell.figure,
        unit: `${cell.figureContext ?? ''} issues`.trim(),
        comparison: leadRest(cell.comparisonLead, cell.comparisonRest),
      };
    }
    case 'metallurgist': {
      const cell = cmetStripCells(state, input.advisory, input.projectSlug).find((row) => row.id === 'cases')!;
      return {
        figure: cell.figure,
        unit: cell.figureContext ?? null,
        // The action headline only; its effectiveness line stays on the landing.
        comparison: [{ text: 'met-level · ' }, ...leadRest(cell.comparisonLead, '')],
      };
    }
    case 'senior-metallurgist': {
      const cell = smetStripCells(state, input.advisory, input.projectSlug).find((row) => row.id === 'kb')!;
      return {
        figure: cell.figure,
        unit: cell.figureContext ?? null,
        comparison: [{ text: `${cell.name} · ` }, ...leadRest(cell.comparisonLead, cell.comparisonRest)],
      };
    }
    case 'maintenance': {
      const tile = maintenanceEquipKpis(state).find((row) => row.id === 'maint-eq-1')!;
      // `Critical alerts today` → `critical today` on the figure; the caption names them.
      return {
        figure: tile.value,
        unit: tile.label.toLowerCase().replace(/\s*alerts\s*/, ' '),
        comparison: [{ text: tile.caption ?? '' }],
      };
    }
    case 'cro': {
      const model = performanceForRole(
        'cro',
        { cases: casesWithSession(state, input.advisory), loops: bunkerHillLoops(), actions: RECOMMENDED_ACTIONS },
        state,
        { showMoney: false },
      );
      const kpi = model.kpis.find((row) => /^Loops in Manual/i.test(row.label))!;
      // `Loops in Manual > 2h` → `in manual > 2h` on the figure.
      return {
        figure: kpi.value,
        unit: kpi.label.replace(/^Loops\s*/i, '').toLowerCase(),
        comparison: [{ text: kpi.caption ?? '' }],
      };
    }
    case 'operator': {
      // The Operator landing's Record & submit panel: the reading it flags off
      // its normal range, with the landing's own status word (`below band`,
      // polish.P16 adjudication 11) — parameter, reading, status, range. No
      // count the landing does not print.
      const flagged = operatorFlaggedReading(input.state);
      if (!flagged) return { figure: '', unit: null, comparison: [] };
      return {
        figure: flagged.reading,
        unit: flagged.parameter,
        comparison: [
          { text: flagged.status.toLowerCase(), strong: true, tone: 'warn' },
          { text: ` · normal ${flagged.range}` },
        ],
      };
    }
    default: {
      const role = dashboardForRole(roleId);
      const tile = role.kpis.find((row) => row.id === 'ops-al') ?? role.kpis[0];
      if (!tile) return { figure: '', unit: null, comparison: [] };
      const card = kpiCardPropsFromTile(tile);
      return { figure: card.value, unit: card.label.toLowerCase(), comparison: [{ text: card.sub ?? '' }] };
    }
  }
}

/** A non-plant project's Workflows page is the foundation one: its `N total`
 *  count, then its first row's name and runs, as that page prints them. A page
 *  with no workflow has no figure to lead with, so the row is not drawn (a zero
 *  is absence rendered as content). */
function foundationWorkflowsRow(projectSlug: string): RowRead | null {
  const { total, first } = foundationWorkflowsLead(projectModulesSeed(projectSlug).workflows);
  if (total === 0) return null;
  return {
    figure: String(total),
    unit: total === 1 ? 'workflow' : 'workflows',
    comparison: first
      ? [{ text: first.name, strong: true }, { text: ` · ${first.runsCount} run${first.runsCount === 1 ? '' : 's'}` }]
      : [],
  };
}

/** The Workflows catalogue's count (its panel meta), then the Excursion
 *  investigation's own runs, read the way its catalogue row prints them. The
 *  comparison waits while the K6 trace loads. Only the plant project renders
 *  the catalogue; any other project's row reads its own foundation page. */
function workflowsRow(input: ProjectHomeInput): RowRead | null {
  if (projectModulesSeed(input.projectSlug).workflowCatalogue !== 'optiflow') return foundationWorkflowsRow(input.projectSlug);
  const rows = input.workflowCatalogue;
  const unit = rows.length === 1 ? 'workflow' : 'workflows';
  const investigation = rows.find((row) => row.id === INVESTIGATION_DEFINITION_ID);
  if (!investigation || investigation.loading) return { figure: String(rows.length), unit, comparison: [] };
  const count = `${investigation.runCount} run${investigation.runCount === 1 ? '' : 's'}`;
  return {
    figure: String(rows.length),
    unit,
    comparison: [
      { text: investigation.name, strong: true },
      { text: investigation.lastRunLabel ? ` · ${count}, last ${investigation.lastRunLabel}` : ` · ${count}` },
    ],
  };
}

function reportsRow(input: ProjectHomeInput): RowRead {
  // The persona's first Reports tab, the one the route opens on.
  const tab = visibleReportsTabs(input.showMoney)[0]!;
  const template = reportTemplate(tab.id);
  const instance = template ? REPORT_INSTANCES.find((row) => row.templateId === template.id) : undefined;
  if (!template || !instance) return { figure: '', unit: null, comparison: [] };
  // `08 Jun 08:00` → the edition's clock; the day is the cursor's.
  const time = instance.generatedAt.split(' ').at(-1) ?? instance.generatedAt;
  return {
    figure: time,
    unit: instance.deliveredAt ? 'sent' : 'generated',
    comparison: [
      { text: template.title.replace(/ report$/, '') },
      ...(template.nextSend ? [{ text: ' · next ' }, { text: template.nextSend, strong: true }] : []),
    ],
  };
}

function metalAccountingRow(input: ProjectHomeInput): RowRead {
  const view = periodView('day');
  const samplePoints = applySamplePhase(SAMPLE_POINTS, input.diagnosis, input.state);
  const cell = balanceStripCells(view, samplePoints).find((row) => row.id === 'closure')!;
  // `Closure · two-path gap` → `two-path`; the window is the route's own words.
  const basis = (cell.name.split(' · ')[1] ?? '').replace(/\s*gap$/, '');
  const first = (cell.lines[0] ?? []).map((run) => ({ text: run.text, strong: run.strong, tone: toneOf(run.tone) }));
  // The verdict leads (`closes`), then the other metal's gap (`0.1 % Zn`).
  const verdict = first.filter((run) => run.tone);
  const gaps = first.filter((run) => !run.tone).map((run) => ({ ...run, text: run.text.replace(/\s*·\s*$/, '') }));
  return {
    figure: cell.figure,
    unit: cell.unit ? `${cell.unit} gap` : null,
    comparison: [
      ...verdict.flatMap((run) => [run, { text: ' · ' }]),
      ...gaps,
      { text: ` · ${[basis, windowText(view)].filter(Boolean).join(', ')}` },
    ],
  };
}

function meetingRow(input: ProjectHomeInput): RowRead {
  const figures = meetingHeaderFigures(input.state);
  const open = figures.find((row) => row.id === 'open')!;
  const ccc = figures.find((row) => row.id === 'ccc-today')!;
  const meeting = figures.find((row) => row.id === 'meeting')!;
  return {
    figure: open.value,
    unit: 'open 3Ws',
    comparison: [
      { text: `${ccc.value} CCC today`, strong: true },
      { text: ` · met ${meeting.value}` },
    ],
  };
}

function riskRow(): RowRead {
  const cell = riskBand().find((row) => row.id === 'critical-high')!;
  // The ids after each band name stay on Risk; the index keeps the counts.
  const counts = cell.comparison.filter((segment) => segment.strong);
  return {
    figure: cell.figure,
    unit: cell.unit ?? null,
    comparison: counts.flatMap((segment, index) => [
      ...(index > 0 ? [{ text: ' · ' }] : []),
      { text: segment.text, strong: true, tone: segment.bad ? ('bad' as const) : undefined },
    ]),
  };
}

function planningRow(input: ProjectHomeInput): RowRead {
  const cell = planningBandCells(
    monthlyPlanView(input.state),
    annualPlanView(input.state),
    rollingForecastView(input.state),
    input.state,
  )
    .find((row) => row.id === 'to-date')!;
  return {
    figure: cell.figure,
    unit: cell.unit ?? null,
    comparison: [
      { text: cell.lead, strong: true, tone: cell.leadTone === 'warn' ? 'warn' : undefined },
      { text: `${cell.rest} · ${cell.name}` },
    ],
  };
}

function costRow(): RowRead {
  const period = 'month' as const;
  const cell = costStrip(period).find((row) => row.name === 'Operating cost')!;
  return {
    figure: cell.figure,
    unit: 'cost',
    comparison: [
      { text: cell.lead, strong: true, tone: cell.leadTone === 'warn' ? 'warn' : undefined },
      { text: ` ${cell.rest} · ${costPeriod(period).dateRange.split(' · ')[0]}` },
    ],
  };
}

function complianceRow(input: ProjectHomeInput): RowRead {
  const fact = hseHeaderFact(input.state);
  const inspections = hseBand(input.state).find((row) => row.id === 'inspections')!;
  const since = fact.comparison.find((run) => run.strong)?.text ?? '';
  return {
    figure: String(fact.days),
    unit: `${fact.unit} ${fact.label}`,
    comparison: [
      { text: `since ${since} · ` },
      { text: `${inspections.figure} ${inspections.of ?? ''}`.trim(), strong: true },
      { text: ` ${inspections.name.split(' · ')[0]!.replace(/^HSE /, '')}` },
    ],
  };
}

function plantModelRow(input: ProjectHomeInput): RowRead {
  const { state, phase, diagnosis } = input;
  const ranks = applyUtilization(bunkerHillUtilization(), phase, state);
  const strip = plantStrip(ranks, STREAMS, RECIRCULATING_LOOPS, bunkerHillSubprocesses(), applyUnits(bunkerHillUnits()), readingsAt(state));
  const samples = sampleChips(applySamplePhase(bunkerHillSamplePoints(), diagnosis, state));
  const cells = plantFlowCells({ strip, circuits: circuitsView(state), samples, phase });
  const bottleneck = cells.find((cell) => cell.kind === 'circuit' && cell.util);
  if (!bottleneck || bottleneck.kind !== 'circuit') return { figure: '', unit: null, comparison: [] };
  return {
    figure: bottleneck.figure,
    unit: bottleneck.figureContext,
    comparison: [
      ...(bottleneck.comparator ? [{ text: `vs ${bottleneck.comparator} · ` }] : []),
      { text: bottleneck.name, strong: true },
      { text: ` ${bottleneck.util!.label}` },
    ],
  };
}

function ingestionRow(input: ProjectHomeInput, claims: ClaimsSessionState): RowRead {
  const cards = contributionCards(bunkerHillSources(), new Set(), input.projectSlug);
  const band = setupBand(cards, reconcileView(claims, false), bindLiveView(claims), new Set());
  // The artefacts cell is the Files panel's own header; the index reads the tags.
  const cell = band.find((row) => row.id === 'tags')!;
  const [figure, of] = cell.figure.split(/\s+(?=of\b)/);
  return {
    figure: figure ?? cell.figure,
    unit: `${of ?? ''} tags`.trim(),
    comparison: [{ text: 'confirmed · ' }, ...leadRest(cell.comparison.lead ?? '', cell.comparison.rest ?? '')],
  };
}

function processRow(input: ProjectHomeInput): RowRead {
  const loops = controlLoops();
  const table = operatorReadingsTable();
  const cases = casesWithSession(input.state, input.advisory);
  const evidence = loopEvidenceItems(cases, loopTags(loops));
  const readings = table ? readingRows(table, input.state) : [];
  const band = processBand(loops, readings, evidence, input.state.cursor);
  const manual = band.find((row) => row.id === 'manual')!;
  const longest = band.find((row) => row.id === 'longest')!;
  // `vs 4 h rule · LIC4010`: the rule and the loop; the since-clause stays on Process.
  const rule = longest.comparison.slice(0, 4).map((segment) => ({ text: segment.text }));
  return {
    figure: manual.figure,
    unit: `${manual.unit ?? ''} loops`.trim(),
    comparison: [{ text: 'in manual · ' }, { text: `longest ${longest.figure}`, strong: true }, { text: ' ' }, ...rule],
  };
}

function qualityRow(input: ProjectHomeInput): RowRead {
  const raw = tablesForQualityTab('sample-register').find((table) => table.caption.startsWith('Sample register'));
  if (!raw) return { figure: '', unit: null, comparison: [] };
  const pipeline = samplePipelineRead(
    projectRegisterTable(raw, input.state.cadence),
    samplesDueLists(input.state, input.advisory),
    input.state.cursor,
  );
  const lims = pipeline.cells.find((cell) => cell.id === 'lims')!;
  const line = lims.lines[0];
  return {
    figure: lims.figure,
    unit: `${(lims.figureContext ?? '').replace(/\s*streams?$/, '')} in LIMS`.trim(),
    comparison: [{ text: 'streams today · ' }, ...(line ? leadRest(line.lead ?? '', line.rest ?? '') : [])],
  };
}

function maintenanceRow(input: ProjectHomeInput): RowRead {
  const band = maintenanceBand({ points: conditionMonitoringPoints(input.state), tasks: pmTasks(), state: input.state });
  const cell = band.find((row) => row.id === 'alerts')!;
  return {
    figure: cell.figure,
    unit: cell.unit ?? null,
    comparison: firstClause(cell.comparison).map((segment) => ({ text: segment.text, strong: segment.strong })),
  };
}

function downtimeRow(): RowRead {
  const windowId = 'ytd' as const;
  const read = downtimeImpactRead(downtimeEvents(), windowId);
  const { figure, unit } = splitUnit(formatHours(read.lostHours));
  return {
    figure,
    unit: `${unit ?? 'h'} lost`,
    comparison: [
      { text: `${read.availabilityPct.toFixed(1)}%`, strong: true },
      { text: ` mill availability · ${downtimeWindow(windowId).label}` },
    ],
  };
}

function documentsRow(): RowRead {
  const cell = trainingComplianceBand()[0]!;
  return {
    figure: cell.figure,
    unit: cell.unit ? `${cell.unit} trained` : null,
    comparison: cell.comparison.map((segment) => ({ text: segment.text, strong: segment.strong })),
  };
}

/** A row's read, or null when its route has no figure to lead with: the row is
 *  then not drawn at all (a zero is absence rendered as content). */
function rowRead(segment: ProductSegment, input: ProjectHomeInput, claims: ClaimsSessionState): RowRead | null {
  switch (segment) {
    case 'overview': return overviewRow(input);
    case 'plant-model': return plantModelRow(input);
    case 'capacity': return capacityRow(input);
    case 'ingestion': return ingestionRow(input, claims);
    case 'process': return processRow(input);
    case 'quality': return qualityRow(input);
    case 'metal-accounting': return metalAccountingRow(input);
    case 'advisory': return advisoryRow(input);
    case 'compliance': return complianceRow(input);
    case 'risk': return riskRow();
    case 'maintenance': return maintenanceRow(input);
    case 'downtime': return downtimeRow();
    case 'meeting': return meetingRow(input);
    case 'documents': return documentsRow();
    case 'reports': return reportsRow(input);
    case 'workflows': return workflowsRow(input);
    case 'planning': return planningRow(input);
    case 'cost': return costRow();
  }
}

/** The persona's product modules in rail order (`visibleProjectNav`), each with
 *  its owning route's lead reference. Group captions repeat the rail's. A module
 *  whose route has no figure to lead with is not drawn; `on your rail` counts
 *  the rows the index draws. */
export function projectModuleIndex(input: ProjectHomeInput, claims: ClaimsSessionState): ModuleIndexRow[] {
  const { groups } = visibleProjectNav(input.persona);
  const rows: ModuleIndexRow[] = [];
  for (const group of groups) {
    const segments = group.segments.filter((segment): segment is ProductSegment => !CHROME.has(segment));
    const drawn: ModuleIndexRow[] = [];
    for (const segment of segments) {
      const read = rowRead(segment, input, claims);
      if (!read) continue;
      const item = visibleProjectNav(input.persona).items.find((row) => row.segment === segment)!;
      drawn.push({
        segment,
        label: item.label,
        group: group.caption,
        groupContinues: drawn.length > 0,
        ...read,
      });
    }
    rows.push(...drawn);
  }
  return rows;
}

/** Plant artefacts show for every persona; a Files row opens Ingestion only
 *  where the persona's rail has it (comp adjudication 6: the GM gets no link). */
export function filesLinkToIngestion(persona: Persona): boolean {
  return visibleProjectNav(persona).items.some((item) => item.segment === 'ingestion');
}

// ---- Chats -----------------------------------------------------------------------

export type ProjectChatRow = { id: string; title: string; pinned: boolean; when: string };

function isoMinute(ms: number): string {
  return new Date(ms).toISOString().slice(0, 16);
}

/** The project's threads, newest first, each dated from the seed in one
 *  format (`08 Jun`) — never a wall-clock "2h ago". */
export function projectChats(projectSlug: string): ProjectChatRow[] {
  return SEED_THREADS.filter((thread) => thread.projectSlug === projectSlug)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map((thread) => ({
      id: thread.id,
      title: thread.title,
      pinned: thread.pinned,
      when: formatDayMonth(isoMinute(thread.updatedAt) as Parameters<typeof formatDayMonth>[0]),
    }));
}
